import React, { useState, useCallback, useEffect } from 'react';
import {
  Platform,
  View,
  Text,
} from 'react-native';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { SettingsContent } from '../components/SettingsContent';
import { PersonalInfoSettings } from '../components/PersonalInfoSettings';
import { NotificationSettings } from '../components/settings/NotificationSettings';
import { PhoneSettings } from '../components/settings/PhoneSettings';
import { DeveloperAccessSettings } from '../components/settings/DeveloperAccessSettings';
import { settingsEntry } from '../config/settingsCatalog';
import { useLocale } from '../contexts/LocaleContext';
import { colors } from '../theme';

export const SettingsScreen: React.FC = () => {
  const { navigateTo, activeTab, setActiveTab, currentScreen, routeParams } = useAppContext();
  const { t } = useLocale();
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  // Re-check for a pending settings item every time this screen becomes active.
  // This covers two cases:
  //   1. First mount — reads item written by another screen before navigateTo('settings').
  //   2. Screen was already mounted (web SPA) — navigateTo('settings') was a no-op so
  //      the useEffect didn't re-run; we detect the re-activation via currentScreen.
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Panel entries open here; anything else (e.g. a pending item another
  // screen's side pane asked for) goes through the settings catalogue.
  const handleSettingsItemPress = useCallback((itemId: string) => {
    const entry = settingsEntry(itemId);
    if (entry?.panel) setActiveItemId(entry.panel);
    else if (entry?.screen) navigateTo(entry.screen);
  }, [navigateTo]);

  useEffect(() => {
    if (currentScreen !== 'settings') return;
    if (typeof routeParams?.settingsItem === 'string' && ['profile', 'phone', 'notifications', 'developer'].includes(routeParams.settingsItem)) {
      handleSettingsItemPress(routeParams.settingsItem);
      return;
    }
    const checkPendingSettingsItem = async () => {
      try {
        const { getPendingSettingsItem } = await import('../utils/tempStorage');
        const pendingItem = await getPendingSettingsItem();
        if (pendingItem) {
          handleSettingsItemPress(pendingItem);
        }
      } catch (error) {
        console.error('❌ [SettingsScreen] Error checking pending settings item:', error);
      }
    };
    checkPendingSettingsItem();
  }, [currentScreen, handleSettingsItemPress, routeParams?.settingsItem, routeParams?.assistantNavigationKey]);

  const handleBackToSettings = () => {
    setActiveItemId(null);
  };

  // Renders the active settings detail panel (shared between web middle area and mobile full-screen)
  const renderActiveSettingPanel = () => {
    if (activeItemId === 'profile') {
      return <PersonalInfoSettings onBack={handleBackToSettings} />;
    }
    if (activeItemId === 'phone') {
      return <PhoneSettings onBack={handleBackToSettings} />;
    }
    if (activeItemId === 'notifications') {
      return <NotificationSettings onBack={handleBackToSettings} />;
    }
    if (activeItemId === 'developer') {
      return <DeveloperAccessSettings onBack={handleBackToSettings} />;
    }
    return null;
  };

  const renderMainContent = () => {
    if (Platform.OS !== 'web') {
      // Mobile: show active panel full-screen, or settings menu
      const panel = renderActiveSettingPanel();
      if (panel) return panel;
      return (
        <SettingsContent
          variant="screen"
          showHeader={true}
          onItemPress={handleSettingsItemPress}
          activeItemId={activeItemId}
        />
      );
    }

    // Web: render active setting in the middle content area
    const panel = renderActiveSettingPanel();
    if (panel) return panel;

    // No item selected — show a prompt
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ color: colors.text.secondary as string, fontSize: 16, textAlign: 'center' }}>{t('settings.selectFromMenu')}</Text>
      </View>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab="more"
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      leftPaneContent="settings"
      onLeftPaneClose={() => {}}
      onSettingsItemPress={handleSettingsItemPress}
      activeSettingsItemId={activeItemId}
      rightPaneContent="empty"
    >
      {renderMainContent()}
    </ScreenLayout>
  );
};

